/**
 * Phase 1: Business Context
 * Company overview, products, user types, money flow, key metrics
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Building2, Loader2, Check, DollarSign, Users, TrendingUp, Package } from 'lucide-react';
import { getBusinessOverview, saveBusinessOverview } from '../../services/api';

const Phase1BusinessContext = ({ onComplete, isAdmin }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    business_description: '',
    products_services: '',
    user_types: '',
    money_flow: '',
    key_metrics: '',
    business_terminology: ''
  });

  useEffect(() => {
    fetchBusinessOverview();
  }, []);

  const fetchBusinessOverview = async () => {
    setLoading(true);
    try {
      const data = await getBusinessOverview();
      if (data) {
        setForm({
          company_name: data.company_name || '',
          business_description: data.business_description || '',
          products_services: data.products_services || '',
          user_types: data.user_types || '',
          money_flow: data.money_flow || '',
          key_metrics: data.key_metrics || '',
          business_terminology: data.business_terminology || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch business overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.company_name.trim() || !form.business_description.trim()) {
      toast.error('Please fill in at least Company Name and Business Description');
      return;
    }

    setSaving(true);
    try {
      await saveBusinessOverview(form);
      toast.success('Business context saved successfully');
      
      // Calculate completion percentage
      const fields = Object.values(form);
      const filled = fields.filter(f => f.trim()).length;
      const percentage = Math.round((filled / fields.length) * 100);
      
      if (percentage >= 80) {
        onCompleteRef.current(100);
      } else {
        onCompleteRef.current(percentage);
      }
    } catch (error) {
      toast.error('Failed to save business context');
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    const fields = Object.values(form);
    const filled = fields.filter(f => f.trim()).length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          Phase 1: Business Context
          <span className="ml-auto text-sm font-normal text-gray-400">
            {getProgress()}% complete
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-gray-500">
          Help the AI understand your business. This context will be used in every query to provide better, more relevant answers.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              Company/Project Name *
            </Label>
            <Input
              value={form.company_name}
              onChange={(e) => setForm({...form, company_name: e.target.value})}
              placeholder="MyCompany Inc."
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              Key Metrics You Track
            </Label>
            <Input
              value={form.key_metrics}
              onChange={(e) => setForm({...form, key_metrics: e.target.value})}
              placeholder="e.g., Daily deposits, Active users, Revenue, GGR"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            What does your business do? *
          </Label>
          <Textarea
            value={form.business_description}
            onChange={(e) => setForm({...form, business_description: e.target.value})}
            placeholder="Describe your business in 2-3 sentences. This helps the AI understand context when answering questions."
            rows={3}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              Products/Services
            </Label>
            <Textarea
              value={form.products_services}
              onChange={(e) => setForm({...form, products_services: e.target.value})}
              placeholder="What products or services do you offer?"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              User Types
            </Label>
            <Textarea
              value={form.user_types}
              onChange={(e) => setForm({...form, user_types: e.target.value})}
              placeholder="e.g., Players, Affiliates, Admins, Agents"
              rows={2}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" />
            Money Flow
          </Label>
          <Textarea
            value={form.money_flow}
            onChange={(e) => setForm({...form, money_flow: e.target.value})}
            placeholder="How does money move in your system? e.g., User deposits → Wallet balance → Bets → Winnings → Withdrawals"
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            Business Terminology
          </Label>
          <Textarea
            value={form.business_terminology}
            onChange={(e) => setForm({...form, business_terminology: e.target.value})}
            placeholder="Define any internal terms. e.g., GGR = Gross Gaming Revenue, NGR = Net Gaming Revenue, FTD = First Time Depositor"
            rows={2}
          />
        </div>

        {isAdmin && (
          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-sm text-gray-400">
              Fields marked with * are required
            </span>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save & Continue
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Phase1BusinessContext;
